import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import SelectField from '../common/SelectField.js';
import routeService from '../../services/RouteService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { Route as RouteIcon, ArrowLeft, ChevronUp, ChevronDown, X, Plus } from 'lucide-react';
import Button from '../common/Button.js';

const fieldClass =
  'w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white';

const EditRoute = () => {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    from_location_hub_id: '',
    to_location_hub_id: '',
    via_location_ids: [],
  });
  const [viaPick, setViaPick] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setDataLoading(true);
        const [hubsRes, routeRes] = await Promise.all([
          locationHubService.getAllHubs(1, 1000, { all: 'true' }),
          routeService.getById(routeId),
        ]);
        const list = hubsRes.data?.hubs || hubsRes.data?.data || hubsRes.data || [];
        setHubs(Array.isArray(list) ? list : []);
        const route = routeRes.data || routeRes;
        setFormData({
          name: route.name || '',
          from_location_hub_id: route.from_location_hub_id || '',
          to_location_hub_id: route.to_location_hub_id || '',
          via_location_ids: route.via_location_ids || [],
        });
      } catch (err) {
        Alert.error('Failed to load route');
        console.error(err);
        navigate('/routes');
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, [routeId, navigate]);

  const hubLabel = (hub) =>
    `${hub.location}${hub.location_code ? ` (${hub.location_code})` : ''}`;

  const availableViaHubs = hubs.filter((h) => {
    const id = h.id;
    if (!id) return false;
    if (String(id) === String(formData.from_location_hub_id)) return false;
    if (String(id) === String(formData.to_location_hub_id)) return false;
    if (formData.via_location_ids.includes(id)) return false;
    return true;
  });

  const addViaStop = () => {
    if (!viaPick) return;
    const id = Number(viaPick);
    setFormData((prev) => ({
      ...prev,
      via_location_ids: [...prev.via_location_ids, id],
    }));
    setViaPick('');
  };

  const removeVia = (id) => {
    setFormData((prev) => ({
      ...prev,
      via_location_ids: prev.via_location_ids.filter((x) => x !== id),
    }));
  };

  const moveVia = (index, direction) => {
    setFormData((prev) => {
      const next = [...prev.via_location_ids];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, via_location_ids: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.from_location_hub_id || !formData.to_location_hub_id) {
      Alert.error('From and to locations are required');
      return;
    }
    if (String(formData.from_location_hub_id) === String(formData.to_location_hub_id)) {
      Alert.error('From and to locations must be different');
      return;
    }

    setLoading(true);
    try {
      await routeService.update(routeId, {
        name: formData.name.trim() || null,
        from_location_hub_id: Number(formData.from_location_hub_id),
        to_location_hub_id: Number(formData.to_location_hub_id),
        via_location_ids: formData.via_location_ids,
      });
      Alert.success('Route updated successfully!');
      setTimeout(() => navigate(`/routes/view/${routeId}`), 1200);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update route');
    } finally {
      setLoading(false);
    }
  };

  const pathPreview = () => {
    const from = hubs.find((h) => String(h.id) === String(formData.from_location_hub_id));
    const to = hubs.find((h) => String(h.id) === String(formData.to_location_hub_id));
    const via = formData.via_location_ids
      .map((id) => hubs.find((h) => h.id === id))
      .filter(Boolean)
      .map((h) => h.location);
    const parts = [];
    if (from) parts.push(from.location);
    parts.push(...via);
    if (to) parts.push(to.location);
    return parts.length ? parts.join(' → ') : '—';
  };

  if (dataLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading route..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
            <RouteIcon className="w-6 h-6 text-secondary shrink-0" />Edit Route
          </h1>
          <button
            onClick={() => navigate('/routes')}
            className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt hover:bg-buttonBg border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap shrink-0"
          >
            <span className="group-hover:-translate-x-1 transition-transform"><ArrowLeft className="w-4 h-4" /></span>
            Back to Routes
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-6 sm:space-y-8"
        >
          {/* Route Information */}
          <div>
            <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Route Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            <div className="sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Route Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className={fieldClass}
              />
            </div>

            <div className="min-w-0">
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                From Location <span className="text-danger font-bold">*</span>
              </label>
              <SelectField
                variant="form"
                value={formData.from_location_hub_id}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    from_location_hub_id: e.target.value,
                    via_location_ids: p.via_location_ids.filter(
                      (id) => String(id) !== String(e.target.value)
                    ),
                  }))
                }
                className={fieldClass}
                required
              >
                <option value="">Select origin</option>
                {hubs.map((hub) => (
                  <option key={hub.id} value={hub.id}>
                    {hubLabel(hub)}
                  </option>
                ))}
              </SelectField>
            </div>

            <div className="min-w-0">
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                To Location <span className="text-danger font-bold">*</span>
              </label>
              <SelectField
                variant="form"
                value={formData.to_location_hub_id}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    to_location_hub_id: e.target.value,
                    via_location_ids: p.via_location_ids.filter(
                      (id) => String(id) !== String(e.target.value)
                    ),
                  }))
                }
                className={fieldClass}
                required
              >
                <option value="">Select destination</option>
                {hubs.map((hub) => (
                  <option key={hub.id} value={hub.id}>
                    {hubLabel(hub)}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>

          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Via Stops (travel order)
            </label>
            <div className="flex flex-row items-stretch gap-2 mb-2 sm:mb-3">
              <SelectField
                variant="form"
                value={viaPick}
                onChange={(e) => setViaPick(e.target.value)}
                className={`${fieldClass} flex-1`}
              >
                <option value="">Add middle stop…</option>
                {availableViaHubs.map((hub) => (
                  <option key={hub.id} value={hub.id}>
                    {hubLabel(hub)}
                  </option>
                ))}
              </SelectField>
              <Button
                type="button"
                variant="secondary"
                icon={Plus}
                mobileIconOnly
                mobileLabel="Add"
                onClick={addViaStop}
                disabled={!viaPick}
              >
                Add Stop
              </Button>
            </div>

            {formData.via_location_ids.length === 0 ? (
              <p className="text-[11px] sm:text-xs text-gray-500">No middle stops — direct from → to.</p>
            ) : (
              <ul className="space-y-1.5 sm:space-y-2">
                {formData.via_location_ids.map((id, index) => {
                  const hub = hubs.find((h) => h.id === id);
                  return (
                    <li
                      key={`${id}-${index}`}
                      className="flex items-center justify-between gap-2 px-2.5 sm:px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 min-w-0"
                    >
                      <span className="text-xs sm:text-sm text-gray-800 truncate min-w-0">
                        {index + 1}. {hub ? hubLabel(hub) : id}
                      </span>
                      <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => moveVia(index, -1)}
                          className="p-1.5 rounded-lg hover:bg-white border border-transparent hover:border-gray-200"
                          title="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveVia(index, 1)}
                          className="p-1.5 rounded-lg hover:bg-white border border-transparent hover:border-gray-200"
                          title="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeVia(id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                          title="Remove"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="rounded-xl bg-orange-50 border border-orange-100 px-3 sm:px-4 py-2.5 sm:py-3">
            <p className="text-[11px] sm:text-xs font-medium text-orange-800 mb-0.5 sm:mb-1">
              Travel path preview
            </p>
            <p className="text-xs sm:text-sm text-orange-900 font-semibold break-words">
              {pathPreview()}
            </p>
          </div>
          </div>

          <div className="flex flex-row items-center justify-end gap-2 sm:gap-3 pt-1 sm:pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/routes/view/${routeId}`)}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Saving…' : 'Update Route'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default EditRoute;
